/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { inviteUser } from "../../api/mutations";
import { useLanguage } from "../../contexts/LanguageContext";
import { getApiErrorMessages } from "../../utils/apiErrorReasons";
import { SideSheet, ErrorMessages } from "../../components/ui/Modal";
import {
  buttonClass,
  inputClass,
  labelClass,
} from "../../components/ui/styles";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { email: "", role: "member", scannerOnly: false };

// Invites someone by email. The API creates the user and the store_users row
// at once and mails them, so there is no pending state to show afterwards.
export default function InviteDialog({ open, onClose, storeId, onInvited }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState(false);

  const mutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["store-users", storeId] });
      onInvited?.(variables.userData.email);
      reset();
      onClose();
    },
  });

  const emailError = !form.email.trim()
    ? t("validation.emailRequired")
    : !EMAIL_PATTERN.test(form.email.trim())
      ? t("validation.invalidEmail")
      : "";

  function reset() {
    setForm(EMPTY);
    setTouched(false);
    mutation.reset();
  }

  const close = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (emailError) return;
    mutation.mutate({
      storeId,
      userData: {
        email: form.email.trim(),
        role: form.role,
        // Only a member can be restricted; the API ignores it for an admin.
        dashboard_access: !(form.role === "member" && form.scannerOnly),
      },
    });
  };

  const roles = [
    {
      value: "member",
      label: t("roles.member"),
      hint: t("team.invite.memberHint"),
    },
    {
      value: "admin",
      label: t("roles.admin"),
      hint: t("team.invite.adminHint"),
    },
  ];

  const showEmailError = touched && emailError;

  return (
    <SideSheet
      open={open}
      onClose={close}
      onSubmit={submit}
      title={t("team.invite.title")}
      description={t("team.invite.description")}
      closeLabel={t("common.close")}
      footer={
        <>
          <button
            type="button"
            onClick={close}
            className={buttonClass.secondary}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className={buttonClass.primary}
          >
            {mutation.isPending
              ? t("team.invite.sending")
              : t("team.invite.send")}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>
          {t("common.email")}
          <input
            type="email"
            name="invite_email"
            autoComplete="off"
            spellCheck={false}
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            onBlur={() => setTouched(true)}
            placeholder={t("team.invite.emailPlaceholder")}
            aria-invalid={showEmailError ? true : undefined}
            aria-describedby={showEmailError ? "invite-email-error" : undefined}
            className={`${inputClass} font-normal ${showEmailError ? "border-red-400" : ""}`}
          />
        </label>
        {showEmailError && (
          <span id="invite-email-error" className="text-[13px] text-red-700">
            {emailError}
          </span>
        )}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-[13px] font-semibold text-gray-700">
          {t("common.role")}
        </legend>
        <div className="grid grid-cols-2 gap-2">
          {roles.map((role) => {
            const on = form.role === role.value;
            return (
              <label
                key={role.value}
                className={`flex cursor-pointer flex-col gap-1 rounded-[10px] p-3 ${
                  on
                    ? "border-2 border-indigo-600 bg-indigo-50/40"
                    : "border border-gray-200 bg-white"
                }`}
              >
                <input
                  type="radio"
                  name="invite_role"
                  value={role.value}
                  checked={on}
                  onChange={() =>
                    setForm((f) => ({
                      ...f,
                      role: role.value,
                      scannerOnly:
                        role.value === "admin" ? false : f.scannerOnly,
                    }))
                  }
                  className="sr-only"
                />
                <span className="text-sm font-semibold text-gray-900">
                  {role.label}
                </span>
                <span className="text-xs leading-snug text-gray-600">
                  {role.hint}
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {form.role === "member" ? (
        <label className="flex cursor-pointer items-start gap-3 rounded-[10px] border border-gray-200 bg-white px-3.5 py-3">
          <input
            type="checkbox"
            name="scanner_only"
            checked={form.scannerOnly}
            onChange={(e) =>
              setForm((f) => ({ ...f, scannerOnly: e.target.checked }))
            }
            className="mt-0.5 h-5 w-5 rounded-md border-gray-400 text-indigo-600 focus:ring-indigo-600"
          />
          <span className="flex flex-col gap-0.5">
            <span className="text-sm font-semibold text-gray-900">
              {t("team.scannerOnly.inviteLabel")}
            </span>
            <span className="text-xs leading-snug text-gray-600">
              {t("team.scannerOnly.inviteHelp")}
            </span>
          </span>
        </label>
      ) : (
        <p className="rounded-[10px] bg-gray-50 px-3.5 py-3 text-[13px] leading-normal text-gray-600">
          {t("team.invite.adminNote")}
        </p>
      )}

      {mutation.isError && (
        <ErrorMessages
          messages={getApiErrorMessages(
            mutation.error,
            t,
            "errors.inviteError",
          )}
        />
      )}
    </SideSheet>
  );
}
